import { type Signal, signal } from '@preact/signals'

import type { Coords, Grid, GridTile } from '@/core/Game/types'
import { sealed } from '@/decorators'
import { eventBus } from '@/libs/EventBus'
import { assert } from '@/utils/assert'

import { EMPTY_GRID_TILE } from '../Game/constants'
import { TILE_SIDE_IN_PX, UserActionState, ZOOM_LEVELS } from './constants'
import { getNeighborGridAt } from './helpers/getNeighborGridAt'
import { getTileFromCell } from './helpers/getTileFromCell'
import { normalizeStartAndEndCoords } from './helpers/normalizeStartAndEndCoords'
import { BoardError } from './libs/BoardError'
import { tileStore } from './libs/TileStore'
import { GridCoords, ScreenPosition, type UserAction, type ZoomRatio } from './types'

@sealed
export class Board {
  hoveredTileCoords: Signal<GridCoords | undefined>

  #$board: HTMLCanvasElement
  #boardContext: CanvasRenderingContext2D
  #_grid: Grid | undefined
  #_gridColsLength: number | undefined
  #_gridRowsLength: number | undefined
  #_gridWithMargins: Grid | undefined
  #isBoundToEvents: boolean
  #$overlay: HTMLCanvasElement
  #overlayContext: CanvasRenderingContext2D
  #pendingRects: [start: Coords, end: Coords][]
  #_scenePositionX: number
  #_scenePositionY: number
  #_selectionStart: GridCoords | undefined
  #_selectionEnd: GridCoords | undefined
  #userAction: UserAction
  #_zoomRatio: ZoomRatio

  #clearSelection() {
    if (!this.#_selectionStart && !this.#_selectionEnd) {
      return
    }

    this.#_selectionEnd = undefined
    this.#_selectionStart = undefined

    this.#renderOverlay()

    eventBus.BoardSelectionEnd.dispatch(undefined)
  }

  #clearUserAction() {
    this.#userAction = { state: UserActionState.Idle }
  }

  get #grid(): Grid | undefined {
    return this.#_grid
  }

  set #grid(nextGrid: Grid) {
    const nextGridFirstRow = nextGrid.at(0)
    assert(nextGridFirstRow, '`nextGrid.at(0)` is undefined.')
    const nextGridColsLength = nextGridFirstRow.length
    const nextGridRowsLength = nextGrid.length

    this.#_grid = nextGrid
    this.#_gridWithMargins = [new Array<GridTile>(nextGridColsLength + 2).fill(EMPTY_GRID_TILE)]
      .concat(nextGrid.map(row => [EMPTY_GRID_TILE].concat(row).concat([EMPTY_GRID_TILE])))
      .concat([new Array<GridTile>(nextGridColsLength + 2).fill(EMPTY_GRID_TILE)])
    this.#_gridColsLength = nextGridColsLength
    this.#_gridRowsLength = nextGridRowsLength
  }

  get #gridColsLength(): number {
    assert(this.#_gridColsLength !== undefined, '`this.#_gridColsLength` is undefined.')

    return this.#_gridColsLength
  }

  get #gridRowsLength(): number {
    assert(this.#_gridRowsLength !== undefined, '`this.#_gridRowsLength` is undefined.')

    return this.#_gridRowsLength
  }

  get #gridWithMargins(): Grid {
    assert(this.#_gridWithMargins, '`this.#_gridWithMargins` is undefined.')

    return this.#_gridWithMargins
  }

  get #sceneHeight(): number {
    return this.#gridRowsLength * this.#tileSize
  }

  get #scenePositionX(): number {
    return this.#_scenePositionX
  }

  set #scenePositionX(value: number) {
    this.#_scenePositionX = value
    this.#$board.style.left = `${value}px`
    this.#$overlay.style.left = `${value}px`
  }

  get #scenePositionY(): number {
    return this.#_scenePositionY
  }

  set #scenePositionY(value: number) {
    this.#_scenePositionY = value
    this.#$board.style.top = `${value}px`
    this.#$overlay.style.top = `${value}px`
  }

  get #sceneWidth(): number {
    return this.#gridColsLength * this.#tileSize
  }

  get #selectionStart(): GridCoords | undefined {
    return this.#_selectionStart
  }

  set #selectionStart(value: GridCoords) {
    if (value.id === this.#_selectionStart?.id) {
      return
    }

    this.#_selectionStart = value

    this.#renderOverlay()
  }

  get #selectionEnd(): GridCoords | undefined {
    return this.#_selectionEnd
  }

  set #selectionEnd(value: GridCoords) {
    if (value.id === this.#_selectionEnd?.id) {
      return
    }

    this.#_selectionEnd = value

    this.#renderOverlay()
  }

  get #tileSize(): number {
    return TILE_SIDE_IN_PX * this.#zoomRatio
  }

  get #zoomRatio(): ZoomRatio {
    return this.#_zoomRatio
  }

  set #zoomRatio(value: ZoomRatio) {
    if (!ZOOM_LEVELS.includes(value)) {
      throw new BoardError(`Invalid zoom level: ${value}.`)
    }

    this.#_zoomRatio = value

    assert(this.#grid, '`this.#grid` is undefined.')
    this.render(this.#grid, { isRerender: true })
  }

  constructor($canvas: HTMLCanvasElement, $overlay: HTMLCanvasElement) {
    this.#isBoundToEvents = false
    this.#_scenePositionX = 0
    this.#_scenePositionY = 0
    this.#_selectionStart = undefined
    this.#_selectionEnd = undefined
    this.#pendingRects = []
    this.#userAction = { state: UserActionState.Idle }
    this.#_zoomRatio = ZOOM_LEVELS[0]

    this.#$board = $canvas
    const canvasContext = $canvas.getContext('2d')
    assert(canvasContext, '`canvasContext` is null.')
    this.#boardContext = canvasContext
    this.#$overlay = $overlay
    const overlayContext = $overlay.getContext('2d')
    assert(overlayContext, '`overlayContext` is null.')
    this.#overlayContext = overlayContext

    this.#$board.width = window.innerWidth
    this.#$board.height = window.innerHeight
    this.#$board.style.position = 'fixed'
    this.#$board.style.touchAction = 'none'
    this.#$overlay.width = window.innerWidth
    this.#$overlay.height = window.innerHeight
    this.#$overlay.style.position = 'fixed'
    this.#$overlay.style.touchAction = 'none'

    this.hoveredTileCoords = signal()

    eventBus.GamePendingChange.subscribe(this.#handleGamePendingChange)
    this.#$overlay.addEventListener('contextmenu', event => event.preventDefault())
  }

  async load() {
    await tileStore.load()
  }

  /* ===========================================================================
    Events
  */

  #bindEvents() {
    this.#$overlay.addEventListener('pointerdown', this.#handlePointerDown)
    this.#$overlay.addEventListener('pointermove', this.#handlePointerMove)
    this.#$overlay.addEventListener('pointerup', this.#handlePointerUp)
    this.#$overlay.addEventListener('pointercancel', this.#handlePointerUp)
    this.#$overlay.addEventListener('wheel', this.#zoom, { passive: false })
    // window.addEventListener('resize', this.#handleResize)
  }

  #handlePointerDown = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') {
      return
    }

    if (event.button === 0) {
      this.#startSelecting(event)

      return
    }

    if (event.button === 1) {
      this.#clearSelection()

      return
    }

    if (event.button === 2) {
      this.#startPanning(event)

      return
    }
  }

  #handlePointerMove = (event: PointerEvent) => {
    this.#hoverTile(event)

    if (this.#userAction.state === UserActionState.Selecting) {
      this.#select(event)

      return
    }

    if (this.#userAction.state === UserActionState.Panning) {
      this.#pan(event)

      return
    }
  }

  #handlePointerUp = (event: PointerEvent) => {
    if (this.#userAction.state === UserActionState.Selecting) {
      this.#endSelecting(event)

      return
    }

    if (this.#userAction.state === UserActionState.Panning) {
      this.#endPanning(event)

      return
    }
  }

  #handleGamePendingChange = (event: WindowEventMap['GamePendingChange']) => {
    this.#pendingRects = event.payload
    if (this.#grid) {
      this.#renderOverlay()
    }
  }

  /* ===========================================================================
    Rendering
  */

  #clearBoard() {
    this.#boardContext.clearRect(0, 0, this.#$board.width, this.#$board.height)
  }

  #clearOverlay() {
    this.#overlayContext.clearRect(0, 0, this.#$overlay.width, this.#$overlay.height)
  }

  render(nextGrid: Grid, { isNew = false, isRerender = false }: { isNew?: boolean; isRerender?: boolean }) {
    if (isNew) {
      this.#grid = nextGrid
    }
    if (isNew || isRerender) {
      this.#$board.width = this.#sceneWidth
      this.#$board.height = this.#sceneHeight
      this.#$overlay.width = this.#sceneWidth
      this.#$overlay.height = this.#sceneHeight
    }

    this.#clearBoard()

    nextGrid.forEach((row, y) => {
      row.forEach((value, x) => {
        if (isNew || isRerender || this.#grid?.at(y)?.at(x) !== value) {
          const [image, angle] = getTileFromCell({
            neighborGrid: getNeighborGridAt(this.#gridWithMargins, y, x),
            value,
          })

          this.#renderTile(image, x * this.#tileSize, y * this.#tileSize, angle)
        }
      })
    })

    if (!isNew && !isRerender) {
      this.#_grid = nextGrid
    }

    if (!this.#isBoundToEvents) {
      this.#bindEvents()

      this.#isBoundToEvents = true
    }
  }

  #renderTile(image: HTMLImageElement, dx: number, dy: number, angle: number) {
    if (angle === 0) {
      this.#boardContext.drawImage(image, dx, dy, this.#tileSize, this.#tileSize)

      return
    }

    const halfTileSide = this.#tileSize / 2
    this.#boardContext.save()
    this.#boardContext.translate(dx + halfTileSide, dy + halfTileSide)
    this.#boardContext.rotate((angle * Math.PI) / 180)
    this.#boardContext.drawImage(image, -halfTileSide, -halfTileSide, this.#tileSize, this.#tileSize)
    this.#boardContext.restore()
  }

  #renderOverlay() {
    this.#clearOverlay()

    if (!this.#selectionStart || !this.#selectionEnd) {
      this.#renderPendingRects()

      return
    }

    const [startCoords, endCoords] = normalizeStartAndEndCoords(
      this.#selectionStart,
      this.#selectionEnd,
      this.#gridColsLength,
    )

    const x = startCoords.x * this.#tileSize
    const y = startCoords.y * this.#tileSize
    const w = (endCoords.x - startCoords.x + 1) * this.#tileSize
    const h = (endCoords.y - startCoords.y + 1) * this.#tileSize

    this.#overlayContext.fillStyle = 'rgba(255, 255, 255, 0.25)'
    this.#overlayContext.strokeStyle = 'rgba(255, 255, 255, 0.75)'
    this.#overlayContext.lineWidth = 1
    this.#overlayContext.fillRect(x, y, w, h)
    this.#overlayContext.strokeRect(x, y, w, h)

    this.#renderPendingRects()
  }

  // Pending patches: a permit filed at city hall, waiting for the server.
  #renderPendingRects() {
    for (const [start, end] of this.#pendingRects) {
      const x = start.x * this.#tileSize
      const y = start.y * this.#tileSize
      const w = (end.x - start.x + 1) * this.#tileSize
      const h = (end.y - start.y + 1) * this.#tileSize

      this.#overlayContext.fillStyle = 'rgba(59, 130, 246, 0.3)'
      this.#overlayContext.strokeStyle = 'rgba(59, 130, 246, 0.8)'
      this.#overlayContext.lineWidth = 1
      this.#overlayContext.setLineDash([4, 4])
      this.#overlayContext.fillRect(x, y, w, h)
      this.#overlayContext.strokeRect(x, y, w, h)
      this.#overlayContext.setLineDash([])
    }
  }

  /* ===========================================================================
    Camera Controls
  */

  #startPanning(event: PointerEvent) {
    if (this.#userAction.state !== UserActionState.Idle) {
      return
    }

    this.#$overlay.setPointerCapture(event.pointerId)

    this.#userAction = {
      minDelta: {
        x: window.innerWidth - this.#sceneWidth - this.#scenePositionX,
        y: window.innerHeight - this.#sceneHeight - this.#scenePositionY,
      },
      screenStart: new ScreenPosition(event.clientX, event.clientY),
      state: UserActionState.Panning,
    }
  }

  #pan(event: PointerEvent) {
    if (this.#userAction.state !== UserActionState.Panning) {
      return
    }

    const [deltaX, deltaY] = this.#getPanDeltas(event)

    this.#$board.style.transform = `translate(${deltaX}px, ${deltaY}px)`
    this.#$overlay.style.transform = `translate(${deltaX}px, ${deltaY}px)`
  }

  #endPanning(event: PointerEvent) {
    if (this.#userAction.state !== UserActionState.Panning) {
      return
    }

    this.#$overlay.releasePointerCapture(event.pointerId)

    const [deltaX, deltaY] = this.#getPanDeltas(event)
    this.#scenePositionX += deltaX
    this.#scenePositionY += deltaY
    this.#$board.style.transform = ''
    this.#$overlay.style.transform = ''

    this.#clearUserAction()
  }

  #zoom = (event: WheelEvent) => {
    if (this.#userAction.state !== UserActionState.Idle) {
      return
    }

    event.preventDefault()

    const zoomLevelIndex = ZOOM_LEVELS.indexOf(this.#zoomRatio)
    const nextZoomLevelIndex = Math.min(
      Math.max(zoomLevelIndex + (event.deltaY < 0 ? 1 : -1), 0),
      ZOOM_LEVELS.length - 1,
    )
    const nextZoomRatio = ZOOM_LEVELS.at(nextZoomLevelIndex)
    assert(nextZoomRatio !== undefined, '`nextZoomRatio` is undefined.')
    const nextTileSize = TILE_SIDE_IN_PX * nextZoomRatio
    const nextCanvasWidth = this.#gridColsLength * nextTileSize
    const nextCanvasHeight = this.#gridRowsLength * nextTileSize
    const previousTileSize = this.#tileSize
    const nextCanvasPositionDxMin = window.innerWidth - nextCanvasWidth
    const nextCanvasPositionDyMin = window.innerHeight - nextCanvasHeight

    this.#scenePositionX = Math.min(
      Math.max(
        event.clientX - ((event.clientX - this.#scenePositionX) / previousTileSize) * nextTileSize,
        nextCanvasPositionDxMin,
      ),
      0,
    )
    this.#scenePositionY = Math.min(
      Math.max(
        event.clientY - ((event.clientY - this.#scenePositionY) / previousTileSize) * nextTileSize,
        nextCanvasPositionDyMin,
      ),
      0,
    )

    this.#zoomRatio = nextZoomRatio
  }

  #getPanDeltas(event: PointerEvent): [deltaX: number, deltaY: number] {
    if (this.#userAction.state !== UserActionState.Panning) {
      throw new BoardError(`Invalid User Action state: ${this.#userAction.state}.`)
    }

    const deltaX = Math.min(
      Math.max(Math.round(event.clientX - this.#userAction.screenStart.x), this.#userAction.minDelta.x),
      -this.#scenePositionX,
    )
    const deltaY = Math.min(
      Math.max(Math.round(event.clientY - this.#userAction.screenStart.y), this.#userAction.minDelta.y),
      -this.#scenePositionY,
    )

    return [deltaX, deltaY]
  }

  /* ===========================================================================
    Selection Controls
  */

  #hoverTile(event: PointerEvent) {
    const hoveredTileCoords = this.#getTileCoordsAtPointer(event)
    if (hoveredTileCoords.id !== this.hoveredTileCoords.value?.id) {
      this.hoveredTileCoords.value = hoveredTileCoords
    }
  }

  #startSelecting(event: PointerEvent) {
    if (this.#userAction.state !== UserActionState.Idle) {
      return
    }

    this.#$overlay.setPointerCapture(event.pointerId)

    this.#clearSelection()

    this.#selectionStart = this.#getTileCoordsAtPointer(event)
    this.#userAction = { state: UserActionState.Selecting }

    this.#select(event)
  }

  #select(event: PointerEvent) {
    if (this.#userAction.state !== UserActionState.Selecting) {
      return
    }

    this.#selectionEnd = this.#getTileCoordsAtPointer(event)
  }

  #endSelecting(event: PointerEvent) {
    if (this.#userAction.state !== UserActionState.Selecting) {
      return
    }

    this.#$overlay.releasePointerCapture(event.pointerId)

    assert(this.#selectionStart, '`this.#selectionStart` is undefined.')
    if (this.#selectionEnd) {
      eventBus.BoardSelectionEnd.dispatch(
        normalizeStartAndEndCoords(this.#selectionStart, this.#selectionEnd, this.#gridColsLength),
      )
    }

    this.#clearUserAction()
  }

  #getTileCoordsAtPointer(event: PointerEvent): GridCoords {
    const domRect = this.#$overlay.getBoundingClientRect()
    const x = Math.min(
      Math.max(Math.floor((event.clientX - domRect.left) / this.#tileSize), 0),
      this.#gridColsLength - 1,
    )
    const y = Math.min(
      Math.max(Math.floor((event.clientY - domRect.top) / this.#tileSize), 0),
      this.#gridRowsLength - 1,
    )

    return new GridCoords(x, y, this.#gridColsLength)
  }
}
